import { COMMON_ONE_VERIFY_NETWORK_STATUS_CONFIG } from "@common-shared/config/oneVerify/oneVerifyNetworkStatus.config"

import {
    INetworkStatusRulesFilters,
    NetworkStatusExportRowKey,
    NetworkStatusRuleDisplayFieldKey,
} from "../types/networkStatus.types"
import { GENERAL_CONFIG } from "./general.config.ts"

export enum BulkUploadStage {
    INITIAL = "initial",
    STAGING = "staging",
    PARSING = "parsing",
    PARSING_ERROR = "parsingError",
}

export const NETWORK_STATUS_CONFIG = {
    ...COMMON_ONE_VERIFY_NETWORK_STATUS_CONFIG,

    get defaultErrorFieldColumn(): NetworkStatusRuleDisplayFieldKey {
        return "effectiveDate"
    },

    listPerPageStorageKey:
        "networkStatusRules" +
        GENERAL_CONFIG.browserStorageKeys.policiesResultsPerPageSuffix,

    reviewListPerPageStorageKey:
        "networkStatusReviewRules" +
        GENERAL_CONFIG.browserStorageKeys.policiesResultsPerPageSuffix,

    // Accept attribute for CSV file input (bulk upload).
    csvAccept: ".csv,text/csv,application/csv,text/plain",

    // Template row 0 is the header row (no version line).
    csvHeaderRowIndex: 0,

    /** Page size when exporting rules to CSV (fetch full list in pages). */
    csvExportPageSize: 500,
    createChunkSize: 500,

    /** Base filename for downloaded rules CSV (no extension). */
    csvExportBaseFileName: "network-status-rules",

    // S3 URL for the network status rules CSV template (bulk upload).
    get csvTemplateUrl(): string {
        const baseAssetsUrl = import.meta.env.VITE_S3_ASSETS_BASE_URL

        return `${baseAssetsUrl}/nirvana-network-status-rules-template-v1.1.csv`
    },

    /** CSV row keys that are array-valued (multiple values per cell). */
    get csvArrayRowKeys(): NetworkStatusExportRowKey[] {
        return ["insuranceTypes", "planTypes"]
    },

    // Backend error field key → column key for showing error under the right cell (rules table)
    get errorFieldToColumn(): Record<string, NetworkStatusRuleDisplayFieldKey> {
        return {
            startDate: "effectiveDate",
            endDate: "effectiveDate",
            effectiveDate: "effectiveDate",
            networkStatus: "networkStatus",
            payerId: "payerId",
            providerNpi: "providerNpi",
            thirdPartyAdministratorId: "thirdPartyAdministratorId",
            insuranceTypes: "insuranceTypes",
            planTypes: "planTypes",
            planName: "planName",
            groupName: "groupName",
            state: "state",
            name: "name",
        }
    },

    // Map CSV column header (trimmed) to API request key for bulk upload. Matches nirvana-network-status-rules-template-v1.1.csv.
    get csvHeaderToKey(): Record<string, NetworkStatusExportRowKey> {
        return {
            "Network Status": "networkStatus",
            "Payer ID": "payerId",
            "Provider NPI": "providerNpi",
            "Start Date": "startDate",
            "End Date": "endDate",
            "3rd Party Administrator ID (optional)":
                "thirdPartyAdministratorId",
            "Insurance Type (optional)": "insuranceTypes",
            "Plan Type (optional)": "planTypes",
            "Plan Name (optional)": "planName",
            "Group Name - Group ID (optional)": "groupName",
            "State (optional)": "state",
            "Rule Name (optional)": "name",
        }
    },

    // Map CSV column header (trimmed) to API request key that are ignored for bulk upload
    get csvHeaderToKeyIgnored(): Record<string, NetworkStatusExportRowKey> {
        return {
            ID: "id",
        }
    },

    /** Column headers for CSV export in upload-compatible order (same as template), with ignored columns (e.g. ID) appended at the end. */
    get csvExportHeadersToKey(): Record<string, NetworkStatusExportRowKey> {
        return {
            ...this.csvHeaderToKey,
            ...this.csvHeaderToKeyIgnored,
        }
    },

    // Display labels for network status rules filter chips. Keys match filter keys; payer/thirdPartyAdministrator omitted (display uses payerId/thirdPartyAdministratorId).
    get rulesFiltersDisplayLabels(): Record<
        Exclude<
            keyof INetworkStatusRulesFilters,
            "payer" | "thirdPartyAdministrator"
        >,
        string
    > {
        return {
            networkStatus: "Network status",
            payerId: "Payer",
            providerNpi: "Provider NPI",
            thirdPartyAdministratorId: "Third party administrator",
            insuranceTypes: "Insurance type",
            planTypes: "Plan type",
            planName: "Plan name",
            groupName: "Group name",
            state: "State",
            startDate: "Start date",
            endDate: "End date",
            name: "Rule name",
            order: "Priority",
        }
    },

    get ruleDisplayTableRowKeys(): NetworkStatusRuleDisplayFieldKey[] {
        return [
            "effectiveDate",
            "networkStatus",
            "payerId",
            "providerNpi",
            "thirdPartyAdministratorId",
            "insuranceTypes",
            "planTypes",
            "planName",
            "groupName",
            "state",
            "name",
        ]
    },

    get ruleDisplayAlertPreviewKeys(): NetworkStatusRuleDisplayFieldKey[] {
        return [
            "networkStatus",
            "payerId",
            "providerNpi",
            "thirdPartyAdministratorId",
            "insuranceTypes",
            "planTypes",
            "planName",
            "groupName",
            "state",
            "version",
            "id",
            "startDate",
            "endDate",
        ]
    },
}
